// The grenade form on the board (qa-runs#1171 part 3): while the form is open, the spot aimed at (a tap picks it —
// touch-friendly, one press, no drag), the throw's line from the thrower, the reach a Power of 1 has if it lands true
// (a preview from the form's numbers; the server decides where it really lands), the walls the DM marks broken (a tap
// on a wall toggles it), and after the throw the scatter: a dashed line from the aim to the blast the server laid,
// and, while it waits to go off, the spot the DM taps to move it to (a red cross, a line from where it landed).
// Drawn over the canvas at the board's camera like the pins; it reads only what this person's store holds.
import { useEffect, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent } from 'react';
import { axisPositions, type Area, type Wall } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { boardBus, type BoardViewState } from './bus';
import { blastAim } from './blastAim';

function useView(): BoardViewState | null {
  const [v, setV] = useState<BoardViewState | null>(null);
  useEffect(() => boardBus.onView(setV), []);
  return v;
}

export function BlastAimLayer() {
  const s = useSyncExternalStore(blastAim.subscribe, blastAim.get);
  const { store, role, meId } = useTableState();
  const view = useView();
  if (!s.open || !view || !(view.view.zoom > 0)) return null;
  const z = view.view.zoom;
  const sx = (x: number) => (x - view.view.x) * z;
  const sy = (y: number) => (y - view.view.y) * z;
  const tap = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation(); e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const p = { x: view.view.x + (e.clientX - r.left) / z, y: view.view.y + (e.clientY - r.top) / z };
    if (s.moving) blastAim.pickMove(p); else blastAim.pick(p);
  };
  const walls = role === 'dm' ? ((store.state.walls ?? []) as Wall[]).filter((w) => w.move || w.door) : [];
  // The blast the server laid after this person's throw: an area of theirs that was not on the board before it.
  let landed: { x: number; y: number } | null = null;
  if (s.thrownBefore && s.aimed) {
    const area = Object.values((store.state.areas ?? {}) as Record<string, Area>).find((a) => a.ownerId === meId && a.shape === 'burst' && !s.thrownBefore!.has(a.id));
    const grid = store.state.grid as Parameters<typeof axisPositions>[0];
    if (area) {
      const xs = axisPositions(grid, view.extent, 'x'); const ys = axisPositions(grid, view.extent, 'y');
      const x = xs[area.origin.i]; const y = ys[area.origin.j];
      if (x !== undefined && y !== undefined) landed = { x, y };
    }
  }
  return (
    <>
      <svg className="pointer-events-none absolute inset-0 h-full w-full" data-testid="blast-aim-layer" aria-hidden="true">
        {s.thrower && s.aimed && <line x1={sx(s.thrower.x)} y1={sy(s.thrower.y)} x2={sx(s.aimed.x)} y2={sy(s.aimed.y)} stroke="#fbbf24" strokeWidth={2} strokeDasharray="2 6" />}
        {s.aimed && s.reachPx !== null && !landed && <circle cx={sx(s.aimed.x)} cy={sy(s.aimed.y)} r={Math.max(4, s.reachPx * z)} fill="rgba(251,146,60,0.12)" stroke="#fb923c" strokeWidth={1.5} strokeDasharray="6 4" />}
        {s.aimed && (
          <g data-testid="blast-aim-mark">
            <circle cx={sx(s.aimed.x)} cy={sy(s.aimed.y)} r={9} fill="none" stroke="#fbbf24" strokeWidth={2} />
            <line x1={sx(s.aimed.x) - 13} y1={sy(s.aimed.y)} x2={sx(s.aimed.x) + 13} y2={sy(s.aimed.y)} stroke="#fbbf24" strokeWidth={2} />
            <line x1={sx(s.aimed.x)} y1={sy(s.aimed.y) - 13} x2={sx(s.aimed.x)} y2={sy(s.aimed.y) + 13} stroke="#fbbf24" strokeWidth={2} />
          </g>
        )}
        {landed && s.aimed && (landed.x !== s.aimed.x || landed.y !== s.aimed.y) && (
          <line data-testid="blast-scatter" x1={sx(s.aimed.x)} y1={sy(s.aimed.y)} x2={sx(landed.x)} y2={sy(landed.y)} stroke="#f87171" strokeWidth={2.5} strokeDasharray="8 5" markerEnd="url(#blast-arrow)" />
        )}
        {s.moveTo && (
          <g data-testid="blast-move-mark">
            {landed && <line x1={sx(landed.x)} y1={sy(landed.y)} x2={sx(s.moveTo.x)} y2={sy(s.moveTo.y)} stroke="#ef4444" strokeWidth={2} strokeDasharray="3 4" />}
            <line x1={sx(s.moveTo.x) - 11} y1={sy(s.moveTo.y) - 11} x2={sx(s.moveTo.x) + 11} y2={sy(s.moveTo.y) + 11} stroke="#ef4444" strokeWidth={3} />
            <line x1={sx(s.moveTo.x) - 11} y1={sy(s.moveTo.y) + 11} x2={sx(s.moveTo.x) + 11} y2={sy(s.moveTo.y) - 11} stroke="#ef4444" strokeWidth={3} />
          </g>
        )}
        <defs><marker id="blast-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#f87171" /></marker></defs>
        {walls.filter((w) => s.broken.has(w.id)).map((w) => (
          <line key={w.id} x1={sx(w.a.x)} y1={sy(w.a.y)} x2={sx(w.b.x)} y2={sy(w.b.y)} stroke="#ef4444" strokeWidth={5} strokeDasharray="4 4" />
        ))}
      </svg>
      {/* Picking the spot: the whole board takes one tap. */}
      {(s.aiming || s.moving) && <div className="pointer-events-auto absolute inset-0 cursor-crosshair" style={{ touchAction: 'none' }} data-testid="blast-aim-capture" onPointerDown={tap} />}
      {/* Marking walls: each wall a wide, finger-sized hit line. */}
      {s.marking && (
        <svg className="pointer-events-none absolute inset-0 h-full w-full" data-testid="blast-wall-marks">
          {walls.map((w) => (
            <line key={w.id} x1={sx(w.a.x)} y1={sy(w.a.y)} x2={sx(w.b.x)} y2={sy(w.b.y)} stroke={s.broken.has(w.id) ? '#ef4444' : 'rgba(250,204,21,0.55)'} strokeWidth={18} strokeLinecap="round"
              className="pointer-events-auto cursor-pointer" role="button" aria-label={s.broken.has(w.id) ? 'Broken wall: tap to make it hold' : 'Wall: tap to mark it broken'}
              onPointerDown={(e) => { e.stopPropagation(); blastAim.toggleWall(w.id); }} />
          ))}
        </svg>
      )}
    </>
  );
}
